import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import * as SITE from "@/content/site";

// Coleta todos os textos públicos do arquivo de conteúdo (ignora estruturas internas).
const collect = () => {
    const blocks = [];
    const links = [];
    const walk = (v, path) => {
        if (typeof v === "string") {
            if (/^https?:\/\//.test(v) && !v.includes("images.unsplash.com")) links.push(v);
            else if (v.length > 40 && !v.includes("{{")) blocks.push({ where: path, text: v, has_source: false });
        } else if (Array.isArray(v)) v.forEach((x, i) => walk(x, `${path}[${i}]`));
        else if (v && typeof v === "object") Object.entries(v).forEach(([k, x]) => walk(x, `${path}.${k}`));
    };
    Object.entries(SITE).forEach(([k, v]) => !k.startsWith("_") && k !== "IMG" && walk(v, k));
    return { blocks, links };
};

const Section = ({ title, items, render, testId }) => (
    <section data-testid={testId} className="mb-12">
        <p className="font-serif text-[20px] text-[#EAEAE6] mb-4">{title} <span className="font-sans text-[12px] text-[#7E8978]">({items.length})</span></p>
        {items.length === 0 ? <p className="font-sans text-[13px] text-[#7E8978]">Nenhum ponto identificado.</p> : <ul className="flex flex-col">{items.map(render)}</ul>}
    </section>
);

const Li = ({ children }) => <li className="border-t border-[#1B3025] py-3 font-sans text-[13px] leading-relaxed text-[#D7D1BF]">{children}</li>;

const AdminSiteReview = () => {
    const [report, setReport] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get("/admin/site-review/latest").then((r) => setReport(r.data));
    }, []);

    const run = async () => {
        setBusy(true);
        setErr("");
        try {
            setReport((await api.post("/admin/site-review", collect(), { timeout: 180000 })).data);
        } catch (e) {
            setErr("Não foi possível concluir a revisão agora.");
        }
        setBusy(false);
    };

    const links = (report?.links || []).filter((l) => l.status !== "ok");
    return (
        <main data-testid="admin-site-review" className="max-w-3xl mx-auto px-6 py-14">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <p className="font-serif text-[22px] text-[#EAEAE6]">Revisar conteúdo científico</p>
                <button type="button" onClick={run} disabled={busy} data-testid="site-review-run" className="font-sans text-[11px] uppercase tracking-[0.16em] text-[#C7A659] border border-[#C7A659]/50 rounded-full px-4 py-2 hover:bg-[#C7A659]/10 disabled:opacity-50">
                    {busy ? "revisando… (até 2 min)" : "Executar revisão"}
                </button>
            </div>
            <p className="font-sans text-[12px] text-[#7E8978] mb-10">Gera apenas um relatório. Nada é alterado ou publicado. Itens sem verificação confiável aparecem como “revisão manual necessária”. Uma revisão automática roda todo dia às 7h (Manaus) e envia o relatório por e-mail; ela usa os textos fixos do site salvos na última execução manual — execute manualmente ao menos uma vez após cada publicação.</p>
            {err && <p className="font-sans text-[13px] text-[#d9a46b] mb-6">{err}</p>}
            {report && (
                <div data-testid="site-review-report">
                    <p className="font-sans text-[12px] text-[#B0B6A6] mb-10">
                        Relatório de {new Date(report.created_at).toLocaleString("pt-BR")} · {report.summary.blocks} blocos de texto · {report.summary.links} links verificados · {report.summary.broken} quebrados
                    </p>
                    <Section testId="review-links" title="Links com problema ou não verificáveis" items={links} render={(l) => <Li key={l.url}><strong className="text-[#C7A659]">{l.status}</strong>{l.code ? ` (HTTP ${l.code})` : ""} — <a href={l.url} target="_blank" rel="noopener noreferrer" className="underline break-all">{l.url}</a>{l.note ? ` · ${l.note}` : ""}</Li>} />
                    <Section testId="review-outdated" title="Dados potencialmente desatualizados" items={report.outdated} render={(o, i) => <Li key={i}><strong>{o.where}</strong> — {o.issue} · <em>{o.status}</em></Li>} />
                    <Section testId="review-numbers" title="Números sem fonte identificada no bloco" items={report.numbers_without_source} render={(o, i) => <Li key={i}><span className="text-[#7E8978]">{o.where}</span><br />“{o.excerpt}” · <em>{o.status}</em></Li>} />
                    <Section testId="review-critical" title="Leitura crítica por IA (revisão humana necessária)" items={report.critical_reading} render={(o, i) => <Li key={i}><span className="text-[#7E8978]">{o.where} · {o.kind}</span><br />{o.excerpt && <>“{o.excerpt}”<br /></>}<strong>{o.issue}</strong>{o.suggestion && <><br /><span className="text-[#B0B6A6]">→ {o.suggestion}</span></>}</Li>} />
                    <Section testId="review-manual" title="Não verificável automaticamente" items={report.not_verifiable} render={(o, i) => <Li key={i}><strong>{o.item}</strong> — {o.status}. {o.note}</Li>} />
                </div>
            )}
        </main>
    );
};

export default AdminSiteReview;
